import { Autocomplete, Chip, TextField, Tooltip, Stack } from "@mui/material";
import { BRAND } from "../../styles/theme";
import type { CollectorSummaryRow } from "../../services/collectorStats";

/** Un vendor iid y los collectors que lo tienen como `vendor` (casi siempre uno). */
export interface VendorOption { iid: number; ids: string[] }

const chipSx = { fontWeight: 700, fontSize: 11, height: 22 };

export function vendorOptions(rows: CollectorSummaryRow[]): VendorOption[] {
  const by = new Map<number, string[]>();
  for (const r of rows) {
    if (r.vendor_iid == null) continue;
    by.set(r.vendor_iid, [...(by.get(r.vendor_iid) ?? []), r.id]);
  }
  return [...by.entries()].map(([iid, ids]) => ({ iid, ids: ids.sort() })).sort((a, b) => a.ids[0].localeCompare(b.ids[0]));
}

const label = (o: VendorOption) => (o.ids.length ? o.ids.join(" / ") : `#${o.iid}`);

function VendorChip({ o, primary, onDelete }: { o: VendorOption; primary: boolean; onDelete?: (event: any) => void }) {
  const missing = o.ids.length === 0;
  const sx = primary ? { bgcolor: BRAND.pendingBg, color: BRAND.pendingText }
    : missing ? { bgcolor: "#FEE2E2", color: "#B91C1C" } : { bgcolor: "#EEE", color: "#333" };
  const title = missing ? `vendor ${o.iid} does not exist as a collector` : `vendor ${o.iid}${primary ? " · main collector" : ""}`;
  return <Tooltip title={title}><Chip size="small" label={label(o)} onDelete={onDelete} sx={{ ...chipSx, ...sx }} /></Tooltip>;
}

function resolve(iids: number[], options: VendorOption[]): VendorOption[] {
  const by = new Map(options.map((o) => [o.iid, o]));
  return iids.map((i) => by.get(i) ?? { iid: i, ids: [] });
}

/** Chips de solo lectura: principal primero, después el resto. */
export function CollectorChips({ iids, primary, options }: { iids: number[]; primary: number | null; options: VendorOption[] }) {
  const sorted = [...iids].sort((a, b) => (a === primary ? -1 : b === primary ? 1 : 0));
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {resolve(sorted, options).map((o) => <VendorChip key={o.iid} o={o} primary={o.iid === primary} />)}
    </Stack>
  );
}

/**
 * Selector múltiple de collectors existentes. El principal (`primary`) está
 * siempre incluido y no se puede quitar. Ids que ya no existen se muestran en
 * rojo para que se saquen (el backend los rechaza).
 */
export function CollectorPicker({ options, primary, value, onChange, disabled }: {
  options: VendorOption[]; primary: number; value: number[]; onChange: (v: number[]) => void; disabled?: boolean;
}) {
  const selected = resolve([primary, ...value.filter((i) => i !== primary)], options);
  return (
    <Autocomplete
      multiple disabled={disabled} options={options} value={selected} filterSelectedOptions
      getOptionLabel={label} isOptionEqualToValue={(a, b) => a.iid === b.iid}
      onChange={(_, v) => onChange([primary, ...v.map((o) => o.iid).filter((i) => i !== primary)])}
      renderTags={(v, getTagProps) => v.map((o, index) => {
        const { key, onDelete } = getTagProps({ index });
        return <VendorChip key={key} o={o} primary={o.iid === primary} onDelete={o.iid === primary ? undefined : onDelete} />;
      })}
      renderInput={(params) => <TextField {...params} label="Collectors" placeholder="Add collector…" />}
    />
  );
}
